import { useEffect, useRef, useState } from 'react';

import * as analytics from "@utils/analytics";

import ProfileStep from './ProfileStep';
import PortfolioStep from './PortfolioStep';
import { useScrollLock } from '../utils/scrollLock';
import './Onboarding.css';

const STEPS = [
  { id: 'profile', label: 'Your profile' },
  { id: 'portfolio', label: 'Your portfolio' },
];
const FOCUSABLE = 'button:not([disabled]), input:not([disabled]), textarea:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])';

/**
 * First-time setup after sign-in. Rendered by CommunityAuthProvider; don't mount it yourself.
 * Step 1 (profile) is required, so this can't be dismissed; "Log out" is the way out.
 * Step 2 (portfolio) can be skipped.
 */
export default function OnboardingModal({ uid, initialStep = 'profile', profile, email, onFinish, onLogOut }) {
  useScrollLock(); // shared lock: safe when popups overlap
  const [step, setStep] = useState(initialStep);
  const [savedProfile, setSavedProfile] = useState(profile || {});
  const sheetRef = useRef(null);
  const stepIndex = STEPS.findIndex((s) => s.id === step);

  useEffect(() => {
    analytics.track('Community Onboarding Started', { step: initialStep });
    return () => {
    };
  }, [initialStep]);

  // Scroll to the top and focus the first field whenever the step changes.
  useEffect(() => {
    if (!sheetRef.current) return;
    sheetRef.current.scrollTop = 0;
    const first = sheetRef.current.querySelector('input, textarea');
    if (first) first.focus();
  }, [step]);

  // Keep Tab inside the dialog.
  const onKeyDown = (e) => {
    if (e.key !== 'Tab') return;
    const items = [...sheetRef.current.querySelectorAll(FOCUSABLE)];
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  const profileSaved = (fields) => {
    analytics.track('Community Onboarding Step Completed', { step: 'profile' });
    setSavedProfile((p) => ({ ...p, ...fields }));
    if (savedProfile.portfolioSetup) onFinish(); // portfolio was already answered earlier
    else setStep('portfolio');
  };

  const portfolioDone = (outcome) => {
    analytics.track('Community Onboarding Step Completed', { step: 'portfolio', outcome }); // 'done' | 'skipped'
    onFinish();
  };

  const firstName = (savedProfile.name || '').split(' ')[0];

  return (
    <div className="ob-overlay">
      <div
        className="ob-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ob-title"
        ref={sheetRef}
        onKeyDown={onKeyDown}
      >
        <div className="ob-top">
          <span className="ob-brand" aria-hidden="true">
            WInvest<span>COLLECTIVE</span>
          </span>
          <ol className="ob-steps" aria-label="Setup progress">
            {STEPS.map((s, i) => (
              <li
                key={s.id}
                className={`ob-step${i === stepIndex ? ' ob-step--current' : ''}${i < stepIndex ? ' ob-step--done' : ''}`}
                aria-current={i === stepIndex ? 'step' : undefined}
              >
                <span className="ob-step__num">{i < stepIndex ? '✓' : i + 1}</span>
                {s.label}
                {s.id === 'portfolio' && <span className="ob-step__opt">optional</span>}
              </li>
            ))}
          </ol>
        </div>

        {step === 'profile' && (
          <ProfileStep uid={uid} initial={savedProfile} email={email} onSaved={profileSaved} onLogOut={onLogOut} />
        )}
        {step === 'portfolio' && (
          <PortfolioStep uid={uid} initial={savedProfile.portfolio} firstName={firstName} onDone={portfolioDone} />
        )}
      </div>
    </div>
  );
}
